import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as CountriesActions from '../../../../actions/countries'
import * as PromosActions from '../../../../actions/promos'
import { useAppDispatch } from '../../../../actions/types'
import { colors, Routes } from '../../../../constants'
import {
    Button,
    Column,
    Confirmation,
    Form,
    Headings,
    Input,
    Margin,
    Padding,
} from '../../../../components'
import { Label } from '../../../../components/Form'
import { Sizes } from '../../../../components/Margin'
import type { Promo } from '../../../../types'
import type { PromoFormData } from '../types'
import usePromoParams from '../usePromoParams'
import * as Validation from '../validation'
import useDisplayBrands from './useDisplayBrands'
import useDisplayCountries from './useDisplayCountries'
import useDisplayTypes from './useDisplayTypes'
import usePromoFormData from './usePromoFormData'
import {
    EditButton,
    FlexPadding,
    FullWidthInput,
    FullWidthLabel,
    ImagePreview,
    InputFileButton,
    NameSection,
} from './PromoForm.styles'
import useDisplayStores from './useDisplayStores'
import useDisplayDevices from './useDisplayDevices'
import MultiDateInput from './MultiDateInput'
import ErrorMessages from './ErrorMessages'
import { useAppSelector } from '../../../../reducers'
import { EstateUtils } from '../../../../utils'

type ExceptionFields = 'exception_countries' | 'exception_devices' | 'exception_stores'

export default function PromoForm({ promo }: { promo?: Promo }) {
    const dispatch = useAppDispatch()
    const displayBrands = useDisplayBrands()
    const promoTypes = useDisplayTypes()
    const history = useHistory()
    const { t } = useTranslation()
    const { estate } = usePromoParams()
    const isPreprodEstate = useAppSelector(
        (state) => EstateUtils.isPreprodEstate(state, { estate })
    )
    const {
        areUnsavedChanges,
        formErrors,
        hasAnyExclusion,
        isEditingName,
        onSubmit,
        promoData,
        resetExceptions,
        saveProgress,
        setIsEditingName,
        setPromoData,
        setUnsavedChanges,
        submitError,
    } = usePromoFormData(promo)

    const {
        id: promo_id,
        asset_url,
        date_end,
        date_start,
        group_brand,
        group_skus,
        name,
        priority,
        exception_countries,
        exception_dates,
        exception_devices,
        exception_price_lt,
        exception_price_gt,
        exception_stores,
        exception_skus,
        type: promo_type,
        promo_img,
    } = promoData

    const displayCountries = useDisplayCountries({
        currentValues: exception_countries,
        partialSearch: exception_countries[exception_countries.length - 1],
    })

    const displayStores = useDisplayStores({
        currentValues: exception_stores,
        search: exception_stores[exception_stores.length - 1],
    })

    const displayDevices = useDisplayDevices({
        currentValues: exception_devices,
        search: exception_devices[exception_devices.length - 1],
    })

    React.useEffect(() => {
        dispatch(PromosActions.fetchBrands())
        dispatch(PromosActions.fetchPromoTypes())
        dispatch(CountriesActions.fetchCountries())
    }, [dispatch, estate])

    const buildOnChange = (attr: Exclude<keyof PromoFormData, 'date_end' | 'date_start'>) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            setUnsavedChanges(true)

            let value: string | string[] | number | File | undefined = event.target.value

            if (attr === 'priority') {
                value = parseInt(value, 10)
            }

            if (attr === 'exception_price_lt' || attr === 'exception_price_gt') {
                value = parseFloat(value as string)
            }

            if (attr === 'promo_img') {
                const { files } = event.target

                if (files instanceof Array && files.length) {
                    value = files[0]
                }
            }

            if (attr === 'group_skus' || attr === 'exception_skus') {
                // Transform the last whitespace in the text with a comma,
                // os it starts a new SKU element
                value = (value as string).replace(/(?<=\w)(\s)$/, ',')
                    // Turn into array of SKUs
                    .split(',')
                    // Remove any other character than a digit or a letter
                    .map(sku => sku.replace(/[^\w]/g, ''))
            }

            setPromoData(prevData => {
                return {
                    ...prevData,
                    [attr]: value,
                }
            })
        }
    }

    const onChangeDates = (attr: 'date_end' | 'date_start') => {
        return (value: number) => {
            setUnsavedChanges(true)
            setPromoData(prevData => {
                return {
                    ...prevData,
                    [attr]: new Date(value),
                }
            })
        }
    }

    const onChangeException = (field: ExceptionFields) => (newItem: string) => {
        let displayOptions: { key: string; value: string }[] = []

        if (field === 'exception_countries') {
            displayOptions = displayCountries
        } else if (field === 'exception_devices') {
            displayOptions = displayDevices
        } else if (field === 'exception_stores') {
            displayOptions = displayStores
        } else {
            return
        }

        setPromoData(prevData => {
            const { [field]: selectedData } = prevData

            // If the country is already added, do nothing
            if (selectedData.includes(newItem)) {
                return prevData
            }

            setUnsavedChanges(true)

            // Take the last entry from the exception_countries list
            const lastTextEntry: string | undefined = selectedData[selectedData.length - 1]

            // If there's a last entry, check if there's a partial match from the options
            if (lastTextEntry) {
                const lastTextEntryLower = lastTextEntry.toLowerCase().trim()

                const newItemData = displayOptions.find(option => option.key === newItem)

                // Look for an item that matches the last text entry
                const valueInOptions = newItemData?.key.toLowerCase().trim().includes(lastTextEntryLower)

                // If there's a match, replace the last entry in the list with the right option code
                if (valueInOptions) {
                    const updatedValues = selectedData.slice(0, -1)

                    return {
                        ...prevData,
                        [field]: [
                            ...updatedValues.filter(entry => !!entry),
                            newItem,
                        ],
                    }
                }
            }

            // Default case: add the new item to the end of the list
            return {
                ...prevData,
                [field]: [
                    // Remove empty strings entries
                    ...selectedData.filter(country => !!country),
                    newItem,
                ],
            }
        })
    }

    const onChangeExceptionText = (field: ExceptionFields) =>
        (rawText: string) => {
            setUnsavedChanges(true)
            setPromoData(prevData => {
                return {
                    ...prevData,
                    // If entered a white space, replace it with a comma (new entry)
                    [field]: rawText.replace(/,*\s$/, ',')
                        // Split the string by commas
                        .split(',')
                        // Remove whitespaces
                        .map(item => item.trim()),
                }
            }
        )}

    const onClickResetExceptions = () => {
        if (!isNew) {
            setUnsavedChanges(true)
        }
        resetExceptions()
    }

    const onChangeExceptionDates = (newDate: Date) => {
        setUnsavedChanges(true)
        // setDateDisplay('display')
        setPromoData(prevData => {
            const alreadyInserted = prevData.exception_dates.some(
                date => moment(date).format('DD/MM/YYYY') === moment(newDate).format('DD/MM/YYYY'),
            )

            if (alreadyInserted) {
                return prevData
            }

            const updatedDates = [
                ...prevData.exception_dates,
                newDate,
            ].sort((a, b) => a.getTime() - b.getTime())

            return {
                ...prevData,
                exception_dates: updatedDates,
            }    
        })
    }

    const onDeleteDate = () => {
        setUnsavedChanges(true)
        // setDateDisplay('display')
        setPromoData(prevData => {
            const { exception_dates } = prevData

            return {
                ...prevData,
                exception_dates: exception_dates.slice(0, -1),
            }    
        })
    }

    const onDeletePromo = () => {
        if (!promo_id) {
            return
        }

        const fn = async () => {
            await dispatch(PromosActions.deletePromo(promo_id))

            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_PROMOS, { estate })
            )
        }

        fn()
    }

    const isNew = !promo

    const thereAreErrors =
        (validation: Validation.PromoValidation) => Object.keys(validation).length > 0

    const firstError = thereAreErrors(formErrors)
        ? formErrors[Object.keys(formErrors)[0] as keyof Validation.PromoValidation]
        : undefined

    const SkusLabel = group_skus.length === 0
        ? t('promo-skus')
        : `${t('promo-skus')} (${group_skus.length})`

    const ExcepCountriesLabel = exception_countries.length === 0
        ? t('promo-exception-countries')
        : `${t('promo-exception-countries')} (${exception_countries.length})`

    const ExcepStoresLabel = exception_stores.length === 0
        ? t('promo-exception-stores')
        : `${t('promo-exception-stores')} (${exception_stores.length})`

    const ExcepDevicesLabel = exception_devices.length === 0
        ? t('promo-exception-devices')
        : `${t('promo-exception-devices')} (${exception_devices.length})`

    const ExcepDatesLabel = exception_dates.length === 0
        ? t('promo-exception-dates')
        : `${t('promo-exception-dates')} (${exception_dates.length})`

    const ExcepSkusLabel = exception_skus.length === 0
        ? t('promo-exception-skus')
        : `${t('promo-exception-skus')} (${exception_skus.length})`

    return (
        <Padding>
            <Form>
                <NameSection top bottom>
                    <Column width="40%">
                        {(isNew || isEditingName) && (
                            <Input
                                id="promo-name"
                                name="name"
                                type="text"
                                value={name}
                                onChange={buildOnChange('name')}
                                error={formErrors.name && t(formErrors.name)}
                                placeholder={t('promo-name')}
                            />
                        )}
                        {!(isNew || isEditingName) && (
                            <>
                                <Headings.H2>{name}</Headings.H2> 
                                <EditButton
                                    title={t('promo-edit')}
                                    onClick={() => setIsEditingName(true)}
                                >
                                    <FontAwesomeIcon color={colors.grey} icon="edit" />
                                </EditButton>
                            </>
                        )}
                    </Column>
                    <Column width="60%" right>
                        <ErrorMessages
                            saveProgress={saveProgress}
                            areUnsavedChanges={areUnsavedChanges}
                            errorMessage={firstError ?? submitError?.message}
                        />
                        <Button
                            theme="forestGreen"
                            onClick={onSubmit}
                            disabled={!areUnsavedChanges}
                        >
                            {t('promo-save')}
                        </Button>

                        <Confirmation
                            cancel={t('cancel')}
                            confirmation={`Promo ${name}`}
                            header={t('promo-remove-confirm-header')}
                            onCancel={() => null}
                            onConfirm={onDeletePromo}
                            ok={t('promo-remove-confirm-button')}
                            theme="danger"
                        >
                            <Button
                                theme="danger"
                                disabled={isNew}
                            >
                                {t('promo-delete')}
                            </Button>
                        </Confirmation>
                    </Column>
                </NameSection>
                <Padding color={colors.white}>
                        <Column width="60%">
                            {!isNew && (
                                <Label id="promo-id" label={t('promo-id')} error={null}>
                                    <Input
                                        id="promo-id"
                                        name="promo-id"
                                        type="text"
                                        size="sm"
                                        value={promo_id}
                                        disabled
                                    />
                                </Label>
                            )}
                            <Label id="promo-type" label={t('promo-type')} error={null}>
                                <Input
                                    id="promo-type"
                                    name="promo-type"
                                    type="select"
                                    error={formErrors.type && t(formErrors.type)}
                                    onChange={buildOnChange('type')}
                                    size="sm"
                                    value={promo_type}
                                    values={promoTypes}
                                />
                            </Label>
                            <Label id="promo-start-date" label={t('promo-start-date')} error={null}>
                                <Input
                                    id="promo-start-date"
                                    name="promo-start-date"
                                    type="date"
                                    error={formErrors.date_start && t(formErrors.date_start)}
                                    onChange={onChangeDates('date_start')}
                                    size="sm"
                                    value={date_start}
                                />
                            </Label>
                            <Label id="promo-priority" label={t('promo-priority')} error={null}>
                                <Input
                                    id="promo-priority"
                                    name="promo-priority"
                                    type="number"
                                    error={formErrors.priority && t(formErrors.priority)}
                                    onChange={buildOnChange('priority')}
                                    size="sm"
                                    value={priority}
                                />
                            </Label>
                            <Label id="promo-brand" label={t('promo-brand')} error={null}>
                                <Input
                                    id="promo-brand"
                                    name="promo-brand"
                                    type="select"
                                    error={formErrors.group_brand && t(formErrors.group_brand)}
                                    onChange={buildOnChange('group_brand')}
                                    size="sm"
                                    value={group_brand}
                                    values={displayBrands}
                                />
                            </Label>
                            <Label id="promo-end-date" label={t('promo-end-date')} error={null}>
                                <Input
                                    id="promo-end-date"
                                    name="promo-end-date"
                                    type="date"
                                    error={formErrors.date_end && t(formErrors.date_end)}
                                    onChange={onChangeDates('date_end')}
                                    size="sm"
                                    value={date_end}
                                />
                            </Label>
                        </Column>
                        <Column width="40%" left>
                            <FullWidthLabel id="promo-skus" label={SkusLabel} error={null}>
                                <Input
                                    id="promo-skus"
                                    name="promo-skus"
                                    type="textarea"
                                    error={null}
                                    onChange={buildOnChange('group_skus')}
                                    size="lg"
                                    value={group_skus.join(', ')}
                                    style={{ height: '85px', width: '100%' }}
                                    placeholder="654961, 5216854, 843159..."
                                />
                            </FullWidthLabel>
                        </Column>
                </Padding>
                <Padding top>
                    <FlexPadding color={colors.white}>
                        <Column width="49%">
                            <Padding bottom>
                                <Headings.H3 bold color={colors.bluedark}>
                                    {t('promo-asset')}
                                </Headings.H3>
                            </Padding>
                            {isNew && (
                                <Padding bottom>
                                    <InputFileButton
                                        id="file"
                                        name="file"
                                        type="file"
                                        onChange={buildOnChange('promo_img')}
                                        value={promo_img ? [promo_img] : []}
                                        accept="image/*"
                                    >
                                        {t('promo-upload-asset')}
                                    </InputFileButton>
                                </Padding>
                            )}
                            <Padding color={colors.greylightest} height={promo_img || asset_url ? 'auto' : '425px'}>
                                {promo_img instanceof File && (
                                    <ImagePreview src={URL.createObjectURL(promo_img as File)} alt={name} />
                                )}
                                {!(promo_img instanceof File) && (
                                    <ImagePreview src={asset_url} alt={name} />
                                )}
                            </Padding>
                        </Column>
                        <Column width="49%">
                            <Padding bottom>
                                <Headings.H3 bold color={colors.bluedark}>
                                    {t('promo-exclusions')}
                                </Headings.H3>
                            </Padding>
                            <Button
                                theme="secondaryDanger"
                                fullWidth
                                onClick={onClickResetExceptions}
                                disabled={!hasAnyExclusion}
                                style={{ marginRight: '0' }}
                            >
                                {t('promo-reset-exclusions')}
                            </Button>

                            <FullWidthLabel
                                id="promo-exception-countries"
                                label={ExcepCountriesLabel}
                                error={null}
                            >
                                <Input
                                    id="promo-exception-countries"
                                    name="promo-exception-countries"
                                    type="suggestions"
                                    error={null}
                                    onChange={onChangeException('exception_countries')}
                                    onTextChange={onChangeExceptionText('exception_countries')}
                                    size="full"
                                    value={exception_countries.join(', ')}
                                    options={displayCountries}
                                    maxOptions={20}
                                    autocomplete={false}
                                />
                            </FullWidthLabel>
                            <FullWidthLabel
                                id="promo-exception-stores"
                                label={ExcepStoresLabel}
                                error={null}
                            >
                                <Input
                                    id="promo-exception-stores"
                                    name="promo-exception-stores"
                                    type="suggestions"
                                    error={null}
                                    onChange={onChangeException('exception_stores')}
                                    onTextChange={onChangeExceptionText('exception_stores')}
                                    size="full"
                                    value={exception_stores.join(', ')}
                                    options={displayStores}
                                    maxOptions={20}
                                    autocomplete={false}
                                />
                            </FullWidthLabel>
                            <FullWidthLabel
                                id="promo-exception-devices"
                                label={ExcepDevicesLabel}
                                error={null}
                            >
                                <Input
                                    id="promo-exception-devices"
                                    name="promo-exception-devices"
                                    type="suggestions"
                                    error={null}
                                    onChange={onChangeException('exception_devices')}
                                    onTextChange={onChangeExceptionText('exception_devices')}
                                    size="full"
                                    value={exception_devices.join(', ')}
                                    options={displayDevices}
                                    maxOptions={20}
                                    autocomplete={false}
                                />
                            </FullWidthLabel>
                            {isPreprodEstate && (
                                <FullWidthLabel
                                    id="promo-exception-skus"
                                    label={ExcepSkusLabel}
                                    error={null}
                                >
                                    <Input
                                        id="promo-exception-skus"
                                        name="promo-exception-skus"
                                        type="textarea"
                                        error={null}
                                        onChange={buildOnChange('exception_skus')}
                                        size="lg"
                                        value={exception_skus.join(', ')}
                                        style={{ height: '85px', width: '100%' }}
                                        placeholder={t('promo-exception-skus-placeholder')}
                                    />
                                </FullWidthLabel>
                            )}
                            <FullWidthLabel
                                id="promo-exception-dates"
                                label={ExcepDatesLabel}
                                error={null}
                            >
                                <MultiDateInput
                                    onDeleteDate={onDeleteDate}
                                    onEnterDate={onChangeExceptionDates}
                                    values={exception_dates}
                                />
                            </FullWidthLabel>
                            <FullWidthLabel id="promo-exception-price" label={t('promo-exception-price')} error={null}>
                                <FullWidthInput
                                    id="promo-exception-price-lt"
                                    name="promo-exception-price-lt"
                                    type="text"
                                    error={
                                        formErrors.exception_price_lt
                                            && t(formErrors.exception_price_lt)
                                    }
                                    onChange={buildOnChange('exception_price_lt')}
                                    size="lg"
                                    value={exception_price_lt && Number(exception_price_lt)}
                                    placeholder={t('promo-exception-price-lt')}
                                />
                                <Margin top size={Sizes.xs}>
                                    <FullWidthInput
                                        id="promo-exception-price-gt"
                                        name="promo-exception-price-gt"
                                        type="text"
                                        error={
                                            formErrors.exception_price_gt
                                                && t(formErrors.exception_price_gt)
                                        }
                                        onChange={buildOnChange('exception_price_gt')}
                                        size="lg"
                                        value={exception_price_gt && String(exception_price_gt)}
                                        placeholder={t('promo-exception-price-gt')}
                                    />
                                </Margin>
                            </FullWidthLabel>
                        </Column>
                    </FlexPadding>
                </Padding>
            </Form>
        </Padding>
    )
}
